import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Plus,
  Save,
  ChevronDown,
  Download,
  Upload,
  FileJson,
  FileSpreadsheet,
  ListTodo,
  AlertCircle,
} from 'lucide-react';
import { useParams } from 'react-router-dom';
import { Button } from '../../../../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../../components/ui/dropdown-menu';
import { cn } from '../../../../../lib/utils';
import TokenService from '../../../../../services/token.service';
import SaveConfirmModal from './SaveConfirmModal';

function SkillTreeFooter({
  type,
  onAddSkill,
  onSave,
  disabledSave,
  disabledExport,
  disabledImport,
  sendJson,
  sendCsv,
  removed,
  added,
  updated,
  treeSkills,
  initialTreeData,
  clearData,
  setTreeData,
  setAdded,
  setAlertMessage,
  validateFields,
  setIsDialogOpened,
}) {
  const { department } = useParams();
  const fileInputRef = useRef(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [detailsNames, setDetailsNames] = useState([]);
  const [refresh, setRefresh] = useState(false);

  const alertUser = (e) => {
    e.preventDefault();
    e.returnValue = '';
  };

  useEffect(() => {
    if (added.length > 0 || removed.length > 0 || updated.length > 0 || refresh) {
      window.addEventListener('beforeunload', alertUser);
    }
    return () => {
      window.removeEventListener('beforeunload', alertUser);
    };
  }, [added, removed, updated, refresh]);

  const removedTasksRecursion = (deletedSkills) => {
    const detailNames = [];

    const getDetailsNames = (removedTask) => {
      removedTask.forEach((task) => {
        if (task.children.length > 0) {
          const curTaskName = initialTreeData.find((taskName) => taskName.name === task.name);
          if (curTaskName) {
            const allChildren = initialTreeData.filter((treeData) =>
              curTaskName.children.includes(treeData.itemId),
            );
            getDetailsNames(allChildren);
          }
        }
        detailNames.push(...task.details.map((detail) => detail.name));
      });
    };

    getDetailsNames(deletedSkills);
    setDetailsNames([...detailsNames, ...detailNames]);
  };

  const handleSaveClick = () => {
    setRefresh(true);
    const isError = validateFields(treeSkills);

    if (isError) {
      setAlertMessage('All fields are required!');
      return;
    }

    removedTasksRecursion(removed);
    setShowSaveModal(true);
  };

  const handleConfirmSave = () => {
    setShowSaveModal(false);
    onSave();
    clearData();
    setDetailsNames([]);

    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }

    setRefresh(false);
  };

  const handleDecline = () => {
    setShowSaveModal(false);
    setTreeData(initialTreeData);
    clearData();
    setDetailsNames([]);

    if (fileInputRef.current?.value) {
      setAdded([]);
      fileInputRef.current.value = null;
    }

    setRefresh(false);
  };

  const handleImportFile = ({ target }) => {
    const targetValue = target.value;
    const extension = targetValue.split('.').pop().toLowerCase();

    if (extension === 'csv') {
      sendCsv(target.files[0]);
    } else if (extension === 'json') {
      sendJson(target.files[0]);
    } else {
      setAlertMessage('Invalid file format. Please use CSV or JSON.');
    }
  };

  const getDownloadCsvLink = () => {
    const accessToken = TokenService.getLocalAccessToken();
    return `${process.env.REACT_APP_API_URL}/api/v1/head/grow/${department}/csv/${type}?accessToken=${accessToken}`;
  };

  const getDownloadJsonLink = () => {
    const accessToken = TokenService.getLocalAccessToken();
    return `${process.env.REACT_APP_API_URL}/api/v1/head/grow/${department}/json/${type}?accessToken=${accessToken}`;
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleImportFile}
        accept=".csv,.json"
        className="hidden"
      />

      <div
        className="flex items-center justify-between pt-4 border-t"
        style={{ borderColor: 'hsl(var(--border))' }}
      >
        {/* Add Skill Button */}
        <Button variant="outline" onClick={onAddSkill}>
          <Plus className="h-4 w-4 mr-2" />
          Add Skill
        </Button>

        {/* Right Actions */}
        <div className="flex items-center gap-2">
          {/* Save Button */}
          <Button onClick={handleSaveClick} disabled={disabledSave}>
            <Save className="h-4 w-4 mr-2" />
            Save
          </Button>

          {/* More Actions Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon">
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {/* Export CSV */}
              <DropdownMenuItem disabled={disabledExport} asChild>
                <a href={getDownloadCsvLink()} className="flex items-center">
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Export CSV
                </a>
              </DropdownMenuItem>

              {/* Import CSV */}
              <DropdownMenuItem
                disabled={disabledImport}
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-4 w-4 mr-2" />
                Import CSV/JSON
              </DropdownMenuItem>

              {/* Export JSON */}
              <DropdownMenuItem disabled={disabledExport} asChild>
                <a href={getDownloadJsonLink()} className="flex items-center">
                  <FileJson className="h-4 w-4 mr-2" />
                  Export JSON
                </a>
              </DropdownMenuItem>

              {/* Import Tasks */}
              <DropdownMenuItem onClick={() => setIsDialogOpened(true)}>
                <ListTodo className="h-4 w-4 mr-2" />
                Import Tasks
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Save Confirmation Modal */}
      <SaveConfirmModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onConfirm={handleConfirmSave}
        onDecline={handleDecline}
        added={added}
        updated={updated}
        removed={removed}
        detailsNames={detailsNames}
      />
    </>
  );
}

SkillTreeFooter.propTypes = {
  type: PropTypes.string.isRequired,
  onAddSkill: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  disabledSave: PropTypes.bool.isRequired,
  disabledExport: PropTypes.bool.isRequired,
  disabledImport: PropTypes.bool.isRequired,
  sendJson: PropTypes.func.isRequired,
  sendCsv: PropTypes.func.isRequired,
  removed: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  added: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  updated: PropTypes.arrayOf(PropTypes.string).isRequired,
  treeSkills: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  initialTreeData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  clearData: PropTypes.func.isRequired,
  setTreeData: PropTypes.func.isRequired,
  setAdded: PropTypes.func.isRequired,
  setAlertMessage: PropTypes.func.isRequired,
  validateFields: PropTypes.func.isRequired,
  setIsDialogOpened: PropTypes.func.isRequired,
};

export default SkillTreeFooter;
